import React from 'react';
import {
  CheckCircle2, AlertCircle,
  FileText, Sparkles, Paperclip, GitBranch, Timer
} from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../../../lib/utils';
import {
  card,
  cardTitle,
  surfaceMuted,
  microLabel,
  microLabelWide,
  bodySecondary,
  badgeBase,
  dividerInner,
  motionFadeUp,
  motionStaggerChild,
  accentBarCard,
  statNumber,
} from '../../../lib/design-system';

const btnPrimary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const btnSecondary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const ReviewStep = ({
  questions, assignmentTitle, description, selectedModule,
  totalPoints, dueDate, dueTime, loading, handleSave, courseData, isUngraded,
  attachments = [], onPrevious, completeIn, selectedBranches = ['ALL']
}) => {
  const selectedModuleData = selectedModule
    ? courseData?.syllabus?.modules?.find(m => m._id === selectedModule || m.id === selectedModule)
    : null;
  const moduleName = selectedModuleData
    ? `Module ${selectedModuleData.moduleNumber || ''}: ${selectedModuleData.name}`.replace('Module :', 'Module')
    : "Not selected";

  const totalQuestionPoints = questions.reduce((sum, q) => sum + (q.score || q.points || 0), 0);
  const subjectiveCount = questions.filter(q => q.type === 'subjective').length;
  const objectiveCount = questions.filter(q => q.type === 'objective').length;

  const formatDueDate = () => {
    if (!dueDate) return null;
    const d = new Date(dueDate + "T" + (dueTime || "23:59"));
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const canPublish = assignmentTitle && (questions.length > 0 || attachments.length > 0);

  return (
    <motion.div {...motionFadeUp} className="space-y-5">

      {/* ── Stat Cards Grid ───────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {[
          {
            label: 'Module',
            value: selectedModuleData
              ? `M${selectedModuleData.moduleNumber || ''}`
              : '—',
          },
          {
            label: 'Points',
            value: isUngraded ? '—' : totalPoints,
          },
          {
            label: 'Questions',
            value: questions.length,
          },
          {
            label: 'Due Date',
            value: dueDate
              ? new Date(dueDate + "T" + (dueTime || "23:59")).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : '—',
          },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.1 }}
            whileHover={{ y: -2 }}
            className={cn(card, "p-4 sm:p-5 relative overflow-hidden group")}
          >
            {/* Accent bar — expands on hover */}
            <div className="h-1 w-8 bg-accent1 absolute top-0 left-0 transition-all duration-300 group-hover:w-full" />

            {/* Main value */}
            <div className={cn(statNumber, "mt-2")}>
              {stat.value}
            </div>

            {/* Label */}
            <div className={cn(microLabelWide, "mt-2")}>
              {stat.label}
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── Title + Description ───────────────────────────── */}
      <motion.div {...motionStaggerChild(0, 0.4)} className={cn(card, "overflow-hidden")}>
        <div className="h-1 bg-accent1" />
        <div className="p-5 sm:p-6">
          <div className={cn(microLabel, "mb-2")}>Assignment</div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-gray-900 dark:text-white">
            {assignmentTitle || "Untitled Assignment"}
          </h2>
          {description && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-3">
              {description}
            </p>
          )}
        </div>
      </motion.div>

      {/* ── Info Banners (time limit, branches, ungraded) ── */}
      <div className="flex flex-col gap-2">
        {/* Branches */}
        {selectedBranches.length > 0 && (
          <motion.div
            {...motionStaggerChild(0, 0.4)}
            className={cn(card, "px-4 py-3 flex items-center gap-3")}
          >
            <GitBranch className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-gray-600 dark:text-gray-300">Target:</span>
              {selectedBranches.includes('ALL') ? (
                <span className={cn(badgeBase, "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600")}>
                  All Branches
                </span>
              ) : (
                selectedBranches.map(branch => (
                  <span key={branch} className={cn(badgeBase, "bg-accent1/10 text-accent1 border-accent1/20")}>
                    {branch}
                  </span>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Time Limit */}
        {completeIn && completeIn > 0 && (
          <motion.div
            {...motionStaggerChild(1, 0.4)}
            className={cn(card, "px-4 py-3 flex items-center gap-3")}
          >
            <Timer className="h-4 w-4 text-orange-500 dark:text-orange-400 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Time Limit:{' '}
              <span className="text-orange-600 dark:text-orange-400 font-bold">
                {completeIn >= 60
                  ? `${Math.floor(completeIn / 60)}h ${completeIn % 60 > 0 ? `${completeIn % 60}m` : ''}`
                  : `${completeIn}m`
                }
              </span>
            </span>
          </motion.div>
        )}

        {/* Ungraded */}
        {isUngraded && (
          <motion.div
            {...motionStaggerChild(2, 0.4)}
            className={cn(card, "px-4 py-3 flex items-center gap-3 border-gray-300 dark:border-gray-600")}
          >
            <AlertCircle className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              This is an <span className="font-bold">ungraded</span> assignment — no scores will be recorded.
            </span>
          </motion.div>
        )}
      </div>

      {/* ── Questions Overview ────────────────────────────── */}
      <motion.div {...motionStaggerChild(0, 0.5)} className={cn(card, "overflow-hidden")}>
        <div className={cn("px-5 py-4 flex items-center justify-between border-b", dividerInner)}>
          <h3 className={cardTitle}>Questions</h3>
          <div className="flex items-center gap-2">
            {subjectiveCount > 0 && (
              <span className={cn(badgeBase, "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-700 text-[10px]")}>
                <FileText className="inline h-3 w-3 mr-1" />
                {subjectiveCount} Subjective
              </span>
            )}
            {objectiveCount > 0 && (
              <span className={cn(badgeBase, "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700 text-[10px]")}>
                <CheckCircle2 className="inline h-3 w-3 mr-1" />
                {objectiveCount} Objective
              </span>
            )}
          </div>
        </div>

        {questions.length === 0 ? (
          <div className={cn("p-8 text-center")}>
            <AlertCircle className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
            <p className={bodySecondary}>No questions added</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50 max-h-72 overflow-y-auto">
            {questions.map((q, index) => (
              <div
                key={q.id}
                className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors"
              >
                {/* Number */}
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[11px] font-bold mt-0.5">
                  {index + 1}
                </span>

                {/* Question text */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed line-clamp-2">
                    {q.question}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className={cn(
                      "px-1.5 py-0.5 rounded-sm text-[10px] font-bold border",
                      q.type === 'objective'
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800'
                        : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800'
                    )}>
                      {q.type === 'objective' ? 'MCQ' : 'SUB'}
                    </span>
                    {q.source === 'ai' && (
                      <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800 flex items-center gap-0.5">
                        <Sparkles className="h-2.5 w-2.5" /> AI
                      </span>
                    )}
                    {q.source === 'attachment-auto' && (
                      <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-accent1/10 text-accent1 border border-accent1/20 flex items-center gap-0.5">
                        <Paperclip className="h-2.5 w-2.5" /> Auto
                      </span>
                    )}
                    {q.bloomLevel && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">{q.bloomLevel}</span>
                    )}
                    {q.courseOutcome && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">{q.courseOutcome}</span>
                    )}
                  </div>
                </div>

                {/* Points */}
                {!isUngraded && (q.score || q.points) > 0 && (
                  <span className="flex-shrink-0 text-xs font-bold text-accent1 tabular-nums mt-0.5">
                    {q.score || q.points} pts
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Points footer */}
        {!isUngraded && questions.length > 0 && (
          <div className={cn("px-5 py-3 flex items-center justify-between border-t", dividerInner, surfaceMuted)}>
            <span className={cn(microLabel)}>Total Score</span>
            <span className={cn(
              "text-sm font-black tabular-nums",
              totalQuestionPoints === totalPoints
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-amber-600 dark:text-amber-400'
            )}>
              {totalQuestionPoints} / {totalPoints} pts
            </span>
          </div>
        )}
      </motion.div>

      {/* ── Attachments ───────────────────────────────────── */}
      {attachments.length > 0 && (
        <motion.div {...motionStaggerChild(0, 0.6)} className={cn(card, "overflow-hidden")}>
          <div className={cn("px-5 py-4 flex items-center justify-between border-b", dividerInner)}>
            <h3 className={cardTitle}>Attachments</h3>
            <span className={cn(badgeBase, "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 text-[10px]")}>
              {attachments.length} file{attachments.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {attachments.map((file, index) => (
              <div key={index} className="flex items-center gap-3 px-5 py-3">
                <Paperclip className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                  {file.name || 'Attachment'}
                </span>
                {file.size && (
                  <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* ── Publish CTA ───────────────────────────────────── */}
      <motion.div {...motionStaggerChild(0, 0.7)} className={cn(card, "overflow-hidden")}>
        {canPublish ? (
          <div className="bg-gradient-to-r from-accent1/5 to-transparent dark:from-accent1/10 p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Ready to publish
                </h3>
                <p className={cn(bodySecondary, "mt-0.5")}>
                  Students will be able to view and submit this assignment.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onPrevious}
                  className={cn(btnSecondary, "px-5 py-2.5")}
                >
                  Back
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className={cn(btnPrimary, "px-6 py-2.5")}
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 rounded-sm bg-white/50 animate-pulse" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Publish Assignment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-sm bg-amber-50 dark:bg-amber-900/20">
                <AlertCircle className="h-4 w-4 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                  Not ready to publish
                </h3>
                <ul className="space-y-1">
                  {!assignmentTitle && (
                    <li className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-sm bg-amber-500" />
                      Assignment title is missing
                    </li>
                  )}
                  {questions.length === 0 && attachments.length === 0 && (
                    <li className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-sm bg-amber-500" />
                      Add at least one question or attachment
                    </li>
                  )}
                </ul>
              </div>
              <button
                onClick={onPrevious}
                className={cn(btnSecondary, "px-5 py-2.5 flex-shrink-0")}
              >
                Back
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ReviewStep;
